/**
 * Employment data behind pay estimation and schedule limits.
 *
 * Kept apart from the identity record on purpose: name, e-mail and phone live
 * in the identity provider (Firebase), while these belong to the employment
 * relationship between one person and one restaurant. The same person working
 * two venues has two of these and one identity.
 */

/** Drives whether overtime and the night premium apply at all. */
export type ContractType = 'employment' | 'mandate' | 'b2b'

export type Position = 'waiter' | 'chef' | 'bartender' | 'host' | 'manager'

export interface EmploymentProfile {
  userId: string
  /**
   * Gross hourly rate in the venue's minor currency units, same convention as
   * every other amount in the app, so no value is ever a float of a major
   * unit. How many make one major unit depends on the currency; only
   * `shared/utils/price` knows.
   */
  hourlyRateMinorUnits?: number
  contractType?: ContractType
  position?: Position
  /** Contracted hours per week; the baseline overtime is measured against. */
  weeklyHoursLimit?: number
}

export const CONTRACT_TYPES: ContractType[] = ['employment', 'mandate', 'b2b']

export const POSITIONS: Position[] = [
  'waiter',
  'chef',
  'bartender',
  'host',
  'manager'
]

/**
 * Overtime and the night premium are statutory for an employment contract
 * (Kodeks pracy); a mandate or B2B agreement pays the flat rate unless the
 * contract says otherwise.
 */
export const hasStatutoryPremiums = (contract?: ContractType): boolean =>
  contract === 'employment'
